'use strict';

const libPictView = require('pict-view');

/**
 * ConfigClone — the clone-across-contexts workflow: choose a namespace + a SOURCE scope/context, load
 * its values, pick which to copy, choose a TARGET scope/context (gated by write permission), preview,
 * and apply with a modal confirm. The modern replacement for the legacy "clone config from one place to
 * another". All state lives in AppData.ConfigClone; behaviour in the ConfigClone provider.
 */
class ConfigClone extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get provider()
	{
		return this.pict.providers.ConfigCloneProvider;
	}

	onBeforeRender(pRenderable)
	{
		if (this.provider) { this.provider.deriveRenderState(); }
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	_repaint() { this.render(); }

	_toast(pMessage, pType)
	{
		const tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast(pMessage, { type: pType || 'success' }); }
	}

	setField(pField, pValue) { this.provider.setField(pField, pValue); this._repaint(); }
	toggleValue(pValue) { this.provider.toggleValue(pValue); this._repaint(); }
	selectAll(pChecked) { this.provider.setAllChecked(pChecked); this._repaint(); }

	loadValues() { this.provider.loadSourceValuesAsync().then(() => this._repaint()); }

	back() { window.location.hash = '#/ConfigViewer'; }

	apply()
	{
		const tmpProvider = this.provider;
		const tmpState = tmpProvider.state;
		const tmpPlan = tmpProvider.buildPlan();
		if (!tmpPlan.length) { tmpState.Message = 'Select at least one value to clone.'; this._repaint(); return; }
		const tmpSrc = `${tmpProvider.cv._scopeLabel(tmpState.SourceScope)} (${tmpProvider._idOrSession(tmpState.SourceScope, tmpState.SourceID)})`;
		const tmpTgt = `${tmpProvider.cv._scopeLabel(tmpState.TargetScope)} (${tmpProvider._idOrSession(tmpState.TargetScope, tmpState.TargetID)})`;
		const tmpMessage = `Copy ${tmpPlan.length} value(s) of “${tmpState.NamespaceKey}” from ${tmpSrc} to ${tmpTgt}? Existing overrides at the target will be replaced.`;
		const fApply = () => tmpProvider.applyAsync().then((pResult) =>
		{
			this._repaint();
			if (pResult) { this._toast(`Cloned ${pResult.Succeeded}/${pResult.Succeeded + pResult.Failed} value(s)`, pResult.Failed ? 'error' : 'success'); }
		});
		const tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.confirm === 'function')
		{
			tmpModal.confirm(tmpMessage, { title: 'Clone configuration', confirmLabel: 'Clone', cancelLabel: 'Cancel', dangerous: true })
				.then((pOk) => { if (pOk) { fApply(); } });
		}
		else
		{
			fApply();
		}
	}
}

module.exports = ConfigClone;

module.exports.default_configuration =
{
	ViewIdentifier: 'ConfigClone',
	DefaultRenderable: 'ConfigCloneWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,
	CSS: /*css*/`
		.cc-wrap { padding: 1.25rem 1.5rem 2rem; max-width: 60rem; }
		.cc-head { display:flex; justify-content:space-between; align-items:flex-start; gap:1rem; margin:0 0 1rem; }
		.cc-title { font-size:1.5rem; font-weight:700; margin:0; color:var(--theme-color-text-primary,#1f2733); }
		.cc-subtitle { font-size:0.9rem; color:var(--theme-color-text-muted,#6b7686); margin:0.2rem 0 0; }
		.cc-grid { display:grid; grid-template-columns:1fr 1fr; gap:1rem; }
		@media (max-width: 760px) { .cc-grid { grid-template-columns:1fr; } }
		.cc-card { border:1px solid var(--theme-color-border-default,#d7dce3); border-radius:8px; background:var(--theme-color-background-panel,#fff); padding:0.9rem 1.1rem; }
		.cc-card-head { font-size:0.68rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--theme-color-brand-primary,#2e7d74); margin:0 0 0.7rem; }
		.cc-field { margin:0 0 0.6rem; }
		.cc-label { display:block; font-size:0.68rem; font-weight:600; text-transform:uppercase; letter-spacing:0.04em; color:var(--theme-color-text-muted,#6b7686); margin:0 0 0.15rem; }
		.cc-input, .cc-select { width:100%; font-size:0.9rem; padding:0.3rem 0.45rem; border-radius:6px; border:1px solid var(--theme-color-border-default,#d7dce3); }
		.cc-values { border:1px solid var(--theme-color-border-default,#d7dce3); border-radius:8px; background:var(--theme-color-background-panel,#fff); padding:0.9rem 1.1rem; margin:1rem 0 0; }
		.cc-values-head { display:flex; justify-content:space-between; align-items:center; margin:0 0 0.6rem; }
		.cc-values-title { font-size:0.68rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--theme-color-brand-primary,#2e7d74); }
		.cc-values-list { display:grid; grid-template-columns:repeat(auto-fill, minmax(13rem, 1fr)); gap:0.2rem 1rem; }
		.cc-value { font-size:0.88rem; color:var(--theme-color-text-secondary,#45505f); display:flex; align-items:center; gap:0.4rem; }
		.cc-applybar { margin:1.1rem 0 0; display:flex; align-items:center; gap:1rem; }
		.cc-warn { font-size:0.82rem; color:var(--theme-color-text-muted,#6b7686); }
		.cc-result { border:1px solid var(--theme-color-border-light,#e8ebf0); border-radius:8px; background:var(--theme-color-background-tertiary,#f4f6f9); padding:0.9rem 1.1rem; margin:1rem 0 0; }
		.cc-result-row { font-size:0.88rem; padding:0.15rem 0; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
	`,
	Templates:
	[
		{
			Hash: 'ConfigClone-Wrap',
			Template: /*html*/`
				<div class="cc-wrap">
					<div class="cc-head">
						<div>
							<h1 class="cc-title">Clone configuration</h1>
							<p class="cc-subtitle">Copy a namespace’s configuration from one context to another. The service stores only what differs from the target’s parent.</p>
						</div>
						<button class="button is-light" onclick="_Pict.views.ConfigClone.back()"><span class="icon">{~I:ChevronLeft~}</span><span>Back</span></button>
					</div>
					{~TS:ConfigClone-Message:AppData.ConfigClone.MessageSlot~}
					<div class="cc-grid">
						<div class="cc-card">
							<div class="cc-card-head">1 · Source</div>
							<div class="cc-field"><span class="cc-label">Namespace</span><select class="cc-select" onchange="_Pict.views.ConfigClone.setField('NamespaceKey', this.value)">{~TS:ConfigClone-NsOption:AppData.ConfigClone.NamespaceOptions~}</select></div>
							<div class="cc-field"><span class="cc-label">Scope</span><select class="cc-select" onchange="_Pict.views.ConfigClone.setField('SourceScope', this.value)">{~TS:ConfigClone-ScopeOption:AppData.ConfigClone.SourceScopeOptions~}</select></div>
							<div class="cc-field"><span class="cc-label">Context ID</span><input class="cc-input" value="{~D:AppData.ConfigClone.SourceID~}" onchange="_Pict.views.ConfigClone.setField('SourceID', this.value)"></div>
							<button class="button is-small is-link is-light" onclick="_Pict.views.ConfigClone.loadValues()"><span class="icon">{~I:Search~}</span><span>Load values</span></button>
						</div>
						<div class="cc-card">
							<div class="cc-card-head">2 · Target</div>
							<div class="cc-field"><span class="cc-label">Scope</span><select class="cc-select" onchange="_Pict.views.ConfigClone.setField('TargetScope', this.value)">{~TS:ConfigClone-ScopeOption:AppData.ConfigClone.TargetScopeOptions~}</select></div>
							<div class="cc-field"><span class="cc-label">Context ID</span><input class="cc-input" value="{~D:AppData.ConfigClone.TargetID~}" onchange="_Pict.views.ConfigClone.setField('TargetID', this.value)"></div>
							{~TS:ConfigClone-NoPerm:AppData.ConfigClone.NoTargetPermSlot~}
						</div>
					</div>
					{~TS:ConfigClone-Loading:AppData.ConfigClone.LoadingSlot~}
					{~TS:ConfigClone-Values:AppData.ConfigClone.ValuesSlot~}
					<div class="cc-applybar">
						<button class="button is-link" {~D:AppData.ConfigClone.ApplyDisabledAttr~} onclick="_Pict.views.ConfigClone.apply()"><span class="icon">{~I:Copy~}</span><span>Clone {~D:AppData.ConfigClone.SelectedCount~} value(s) →</span></button>
					</div>
					{~TS:ConfigClone-Result:AppData.ConfigClone.ResultSlot~}
				</div>`
		},
		{
			Hash: 'ConfigClone-NsOption',
			Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.SelectedAttr~}>{~D:Record.Label~}</option>`
		},
		{
			Hash: 'ConfigClone-ScopeOption',
			Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.SelectedAttr~} {~D:Record.DisabledAttr~}>{~D:Record.Label~}</option>`
		},
		{
			Hash: 'ConfigClone-NoPerm',
			Template: /*html*/`<p class="cc-warn">You don’t have permission to write the {~D:Record.Scope~} scope — pick a scope you can write.</p>`
		},
		{
			Hash: 'ConfigClone-Values',
			Template: /*html*/`
				<div class="cc-values">
					<div class="cc-values-head">
						<span class="cc-values-title">Values to clone</span>
						<span>
							<button class="button is-small is-white" onclick="_Pict.views.ConfigClone.selectAll(true)">All</button>
							<button class="button is-small is-white" onclick="_Pict.views.ConfigClone.selectAll(false)">None</button>
						</span>
					</div>
					<div class="cc-values-list">{~TS:ConfigClone-Value:AppData.ConfigClone.ValueRows~}</div>
				</div>`
		},
		{
			Hash: 'ConfigClone-Value',
			Template: /*html*/`<label class="cc-value"><input type="checkbox" {~D:Record.CheckedAttr~} onchange="_Pict.views.ConfigClone.toggleValue('{~D:Record.Value~}')"> {~D:Record.Value~}</label>`
		},
		{
			Hash: 'ConfigClone-Result',
			Template: /*html*/`
				<div class="cc-result">
					<div class="cc-values-title">Result — {~D:Record.Succeeded~} cloned, {~D:Record.Failed~} failed</div>
					{~TS:ConfigClone-ResultRow:AppData.ConfigClone.ResultRows~}
				</div>`
		},
		{
			Hash: 'ConfigClone-ResultRow',
			Template: /*html*/`<div class="cc-result-row"><span class="{~D:Record.StatusClass~}">{~D:Record.Value~}</span> — {~D:Record.StatusText~}</div>`
		},
		{
			Hash: 'ConfigClone-Message',
			Template: /*html*/`<div class="notification is-warning is-soft py-2 px-3 mb-3">{~D:Record.Message~}</div>`
		},
		{
			Hash: 'ConfigClone-Loading',
			Template: /*html*/`<p class="cc-warn mt-3">Working…</p>`
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'ConfigCloneWrap',
			TemplateHash: 'ConfigClone-Wrap',
			ContentDestinationAddress: '#PlatformConfiguration-Application-Container',
		}
	]
};
